import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRight, GitBranch, Plus } from 'lucide-react';
import { Button, Card, Drawer, Field, Input, Kpi, KpiStrip, Pill, SegmentedControl, StatusDot, Switch, Textarea, useToast } from '@os/ui';
import { api, GitHubOutcomeSchema, ScaffoldResultSchema, useApiMutation, useApps, useGitHubStatus, type AdminApp, type GitHubOutcome, type GitHubStatus, type GrantOutcome, type ScaffoldResult } from '../../api';
import { AppIcon } from '../../desktop/AppIcon';
import { Page, PageAlert, PageEmpty } from '../../desktop/Page';
import './admin.css';

/** Clipboard with a fallback for non-secure origins (http://os.localhost has no navigator.clipboard). */
async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch { return false; }
}

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
/** Which `urls.<env>` of the manifest becomes the launcher URL; the API refuses a manifest that lacks it. */
type Environment = 'dev' | 'staging' | 'prod';
const ENVIRONMENTS: { value: Environment; label: string }[] = [{ value: 'dev', label: 'Dev' }, { value: 'staging', label: 'Staging' }, { value: 'prod', label: 'Prod' }];

/** Registered apps: control-panel style — KPI strip, one tile per app (icon, status switch, roles), details drawer. */
export default function Apps() {
  const apps = useApps();
  const github = useGitHubStatus();
  const toast = useToast((s) => s.show);
  const copyRepo = async (url: string) => { const ok = await copyText(url); toast(ok ? `Copied ${url}` : 'Could not copy; select the URL in Details', ok ? 'default' : 'danger'); };
  const [registering, setRegistering] = useState(false);
  const [creating, setCreating] = useState(false);
  const [manifest, setManifest] = useState('');
  const [environment, setEnvironment] = useState<Environment>('dev');
  const [details, setDetails] = useState<AdminApp | null>(null);
  const err = (e: unknown) => toast(e instanceof Error ? e.message : 'Something went wrong', 'danger');
  const patch = useApiMutation(({ id, body }: { id: string; body: Record<string, unknown> }) => api(`/admin/apps/${id}`, { method: 'PATCH', body }), [['admin', 'apps'], ['me']]);
  const register = useApiMutation((b: { manifest: unknown; environment: Environment }) => api('/admin/apps', { method: 'POST', body: b }), [['admin', 'apps'], ['me']]);
  const submit = () => {
    let parsed: unknown;
    try { parsed = JSON.parse(manifest); } catch { toast('Manifest is not valid JSON', 'danger'); return; }
    register.mutate({ manifest: parsed, environment }, { onSuccess: () => { toast('Registered (run app:provision for the database)'); setRegistering(false); setManifest(''); }, onError: err });
  };

  const list = apps.data ?? [];
  const active = list.filter((a) => a.active);
  const roleCount = list.reduce((n, a) => n + a.roles.length, 0);
  const item = (a: AdminApp) => ({ id: a.id, name: a.name, kind: 'app' as const, mark: a.icon ?? a.name.slice(0, 2) });

  return (
    <Page title="Registered apps" subtitle="Every app registered with this OS, from its os-app.json" actions={<><Button icon={<Plus size={15} strokeWidth={1.5} />} onClick={() => setRegistering(true)}>Register from manifest</Button><Button variant="primary" icon={<Plus size={15} strokeWidth={1.5} />} onClick={() => setCreating(true)}>Create app</Button></>}>
      <PageAlert error={apps.error} fallback="Could not load apps" />

      <KpiStrip columns={4}>
        <Kpi label="Registered" value={apps.data ? list.length : '…'} sub="Including the OS itself" />
        <Kpi label="Active" value={apps.data ? active.length : '…'} sub="Shown in the launcher" />
        <Kpi label="Hidden" value={apps.data ? list.length - active.length : '…'} tone={list.length - active.length > 0 ? 'default' : 'muted'} />
        <Kpi label="Roles defined" value={apps.data ? roleCount : '…'} sub="Across all apps" />
      </KpiStrip>

      {apps.data && list.length === 0 && <Card><PageEmpty>No apps registered yet.</PageEmpty></Card>}
      <div className="pg-tiles">
        {list.map((a) => (
          <Card key={a.id} className="apptile" data-inactive={a.active ? '0' : '1'}>
            <div className="apptile-head">
              <AppIcon item={item(a)} size={44} />
              <div className="apptile-name"><b className="truncate">{a.name}</b><span className="mono truncate">{a.id} · {a.url.replace(/^https?:\/\//, '')}</span></div>
              {a.id === 'os' ? <Pill small tone="muted">core</Pill> : <Switch checked={a.active} disabled={patch.isPending} onChange={(v) => patch.mutate({ id: a.id, body: { active: v } }, { onError: err })} label={a.active ? 'Active' : 'Hidden'} />}
            </div>
            <p className="apptile-desc">{a.description || 'No description'}</p>
            <div className="apptile-roles">{a.roles.map((r) => <Pill key={r} small>{r}</Pill>)}{a.roles.length === 0 && <span className="meta-sm">No roles declared</span>}</div>
            <div className="apptile-foot">
              <span className="acct-meta"><StatusDot tone={a.active ? 'ok' : 'off'} />{a.active ? 'Active' : 'Hidden'}<span className="acct-dot">{a.redirect_uris.length} redirect URI{a.redirect_uris.length === 1 ? '' : 's'}</span></span>
              <span className="apptile-actions">
                {a.repo && <Button size="row" variant="ghost" className="apptile-git" icon={<GitBranch size={14} strokeWidth={1.8} />} title={`Copy clone URL — ${a.repo}`} aria-label={`Copy clone URL ${a.repo}`} onClick={() => copyRepo(a.repo!)} />}
                <Button size="row" variant="ghost" onClick={() => setDetails(a)}>Details</Button>
                <Button size="row" icon={<ArrowUpRight size={13} strokeWidth={1.8} />} onClick={() => window.location.assign(a.url)}>Open</Button>
              </span>
            </div>
          </Card>
        ))}
      </div>

      <Drawer open={details !== null} title={details?.name ?? ''} onClose={() => setDetails(null)} footer={details ? <Button size="form" onClick={() => window.location.assign(details.url)}>Open app</Button> : undefined}>
        {details && (
          <>
            <div className="apptile-head"><AppIcon item={item(details)} size={48} /><div className="apptile-name"><b>{details.name}</b><span className="mono">{details.id}</span></div></div>
            <dl className="dl">
              <dt>Status</dt><dd>{details.active ? <Pill tone="ok" small>Active</Pill> : <Pill tone="danger" small>Hidden</Pill>}</dd>
              <dt>Description</dt><dd>{details.description || '—'}</dd>
              <dt>URL</dt><dd><a className="tl-link" href={details.url}>{details.url}</a></dd>
              <dt>Roles</dt><dd style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{details.roles.length ? details.roles.map((r) => <Pill key={r} small>{r}</Pill>) : '—'}</dd>
              <dt>Redirect URIs</dt><dd>{details.redirect_uris.length ? <ul className="mono">{details.redirect_uris.map((u) => <li key={u}>{u}</li>)}</ul> : '—'}</dd>
              <dt>Repository</dt><dd><RepoCell app={details} github={github.data} onCopy={copyRepo} /></dd>
              <dt>Sort order</dt><dd>{details.sort_order}</dd>
              <dt>Registered</dt><dd>{fmtDate(details.registered_at)}</dd>
              <dt>Updated</dt><dd>{fmtDate(details.updated_at)}</dd>
            </dl>
            <div className="meta">Re-register from the app repo (<code>npm run app:register apps/{details.id}</code>) to update roles, URLs or redirect URIs.</div>
          </>
        )}
      </Drawer>

      {creating && <CreateAppModal github={github.data} onClose={() => setCreating(false)} />}

      <Drawer open={registering} title="Register app" onClose={() => setRegistering(false)} footer={<><Button size="form" onClick={() => setRegistering(false)} disabled={register.isPending}>Cancel</Button><Button variant="primary" size="form" onClick={submit} loading={register.isPending} disabled={!manifest.trim() || register.isPending}>Register</Button></>}>
        <div className="meta" style={{ marginBottom: 10 }}>Paste the app's <code>os-app.json</code>. Prefer the CLI (<code>npm run app:register apps/&lt;id&gt;</code>), which also provisions the database.</div>
        <Field label="Environment" hint={`The launcher opens urls.${environment} from the manifest; registration fails if it is missing.`}>
          <SegmentedControl segments={ENVIRONMENTS} value={environment} onChange={setEnvironment} />
        </Field>
        <div style={{ height: 10 }} />
        <textarea value={manifest} onChange={(e) => setManifest(e.target.value)} rows={18} spellCheck={false} className="mono" style={{ width: '100%', border: '1px solid var(--border-strong)', borderRadius: 4, padding: 10 }} />
      </Drawer>
    </Page>
  );
}

const ID_RE = /^[a-z][a-z0-9_]{1,30}$/;
const ICON_RE = /^[A-Za-z0-9]{1,3}$/;
const REPO_RE = /^(?:git@[\w.-]+:[\w./~-]+(?:\.git)?|https:\/\/[\w.-]+\/[\w./~-]+(?:\.git)?|ssh:\/\/git@[\w.-]+\/[\w./~-]+(?:\.git)?)$/;

type Draft = { id: string; name: string; description: string; icon: string; repo: string };

function suggestIcon(name: string, id: string) {
  return (name.trim() || id).replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();
}

function draftError(d: Draft): string | null {
  if (!ID_RE.test(d.id)) return 'App id must be 2–31 characters: a lowercase letter, then letters, digits or underscore';
  if (!d.name.trim() || d.name.trim().length > 80) return 'Display name is required (max 80 characters)';
  if (!ICON_RE.test(d.icon.trim())) return 'Icon must be 1–3 letters or digits';
  if (d.description.trim().length > 200) return 'Description must be 200 characters or fewer';
  if (d.repo.trim() && !REPO_RE.test(d.repo.trim())) return 'Repository must be git@host:path or an https URL, with no <owner> placeholder';
  return null;
}

/** Repository row of the Details drawer: SSH URL + copy, link to GitHub, and a push button when the connector owns (or may own) the repo. */
function RepoCell({ app, github, onCopy }: { app: AdminApp; github?: GitHubStatus; onCopy: (url: string) => void }) {
  const ownedByConnector = !!github?.configured && (app.repo === null || app.repo.toLowerCase().includes(`:${github.owner.toLowerCase()}/`));
  const canPush = ownedByConnector && app.folder !== null && app.id !== 'os';
  return (
    <div className="repo-cell">
      {app.repo ? (
        <>
          <code className="mono">{app.repo}</code>
          <span className="repo-cell-actions">
            <Button size="row" variant="ghost" icon={<GitBranch size={13} strokeWidth={1.8} />} onClick={() => onCopy(app.repo!)}>Copy clone URL</Button>
            {app.repo_web && <a className="tl-link" href={app.repo_web} target="_blank" rel="noreferrer">Open on GitHub</a>}
            {canPush && <PushButton id={app.id} />}
          </span>
        </>
      ) : (
        <>
          <span className="meta-sm">{app.folder ? 'Local repository only (no remote in apps.json)' : 'Not in apps.json on this host'}</span>
          {canPush && <span className="repo-cell-actions"><PushButton id={app.id} /></span>}
        </>
      )}
    </div>
  );
}

/** POST /admin/apps/:id/github — create the GitHub repository if missing, set origin, push main. Idempotent. */
function PushButton({ id, onDone }: { id: string; onDone?: (outcome: GitHubOutcome) => void }) {
  const toast = useToast((s) => s.show);
  const push = useApiMutation((appId: string) => api(`/admin/apps/${appId}/github`, { method: 'POST', schema: GitHubOutcomeSchema }), [['admin', 'apps']]);
  const run = () => push.mutate(id, {
    onSuccess: (data) => {
      const out = GitHubOutcomeSchema.parse(data);
      toast(out.status === 'pushed' ? `Pushed main to ${out.ssh_url}${out.collaborator ? ` — ${grantText(out.collaborator)}` : ''}` : out.status === 'skipped' ? out.reason : out.message, out.status === 'pushed' ? 'default' : 'danger');
      onDone?.(out);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Push to GitHub failed', 'danger'),
  });
  return <Button size="row" icon={<GitBranch size={13} strokeWidth={1.8} />} loading={push.isPending} onClick={run}>Push to GitHub</Button>;
}

/** One line on whether the person who clicked got access to the repository. */
function grantText(g: GrantOutcome | undefined): string {
  if (!g) return '';
  switch (g.status) {
    case 'added': return `${g.login} has ${g.permission} access`;
    case 'invited': return `${g.login} was invited (${g.permission}); accept the invitation on GitHub`;
    case 'skipped': return g.reason;
    case 'failed': return `Access not granted: ${g.message}`;
  }
}

function GitHubOutcomeCell({ outcome, id, onRetry }: { outcome: GitHubOutcome; id: string; onRetry: (outcome: GitHubOutcome) => void }) {
  if (outcome.status === 'pushed') return <div className="repo-cell"><span>Pushed <span className="mono">main</span> to <a className="tl-link" href={outcome.html_url} target="_blank" rel="noreferrer">{outcome.html_url}</a> ({outcome.created ? 'repository created' : 'existing repository'})</span>{outcome.collaborator && <span className={outcome.collaborator.status === 'added' || outcome.collaborator.status === 'invited' ? 'meta-sm' : 'meta-sm'} style={outcome.collaborator.status === 'failed' ? { color: 'var(--danger-fg)' } : undefined}>{grantText(outcome.collaborator)}</span>}</div>;
  if (outcome.status === 'skipped') return <span className="meta-sm">{outcome.reason}</span>;
  return <div className="repo-cell"><span style={{ color: 'var(--danger-fg)' }}>Failed ({outcome.stage}): {outcome.message}</span><span className="repo-cell-actions"><PushButton id={id} onDone={onRetry} /></span></div>;
}

/** Create app: collects the scaffold fields, then runs scripts/app-create.ts on the OS host; with the GitHub connector on, pushes to github.com/<owner>/<id>. */
function CreateAppModal({ github, onClose }: { github?: GitHubStatus; onClose: () => void }) {
  const [draft, setDraft] = useState<Draft>({ id: '', name: '', description: '', icon: '', repo: '' });
  const [iconTouched, setIconTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<ScaffoldResult | null>(null);
  const create = useApiMutation((b: { id: string; name: string; description?: string; icon: string; repo?: string }) => api('/admin/apps/scaffold', { method: 'POST', body: b, schema: ScaffoldResultSchema }), [['admin', 'apps'], ['me']]);
  const set = (patch: Partial<Draft>) => { setError(null); setDraft((d) => ({ ...d, ...patch })); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !create.isPending) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [create.isPending, onClose]);

  const submit = () => {
    const problem = draftError(draft);
    if (problem) { setError(problem); return; }
    setError(null);
    const description = draft.description.trim();
    const repo = draft.repo.trim();
    create.mutate(
      { id: draft.id, name: draft.name.trim(), icon: draft.icon.trim(), ...(description && { description }), ...(repo && { repo }) },
      { onSuccess: (data) => setCreated(ScaffoldResultSchema.parse(data)), onError: (e) => setError(e instanceof Error ? e.message : 'Could not create the app') },
    );
  };

  return createPortal(
    <div className="os-modal-back" onClick={() => { if (!create.isPending) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="create-app-title" className="os-modal" onClick={(e) => e.stopPropagation()}>
        <div className="os-modal-head">
          <div id="create-app-title" className="os-modal-title">Create app</div>
          <button type="button" className="os-modal-close" aria-label="Close" disabled={create.isPending} onClick={onClose}>×</button>
        </div>
        {created ? (
          <>
            <div className="os-modal-body">
              <p className="meta" style={{ margin: 0 }}>Skeleton is in <code>{created.folder}</code>, on git branch <code>main</code>, and the app is on this list.{created.github.status === 'pushed' && <> Clone it with <code>git clone {created.github.ssh_url}</code>.</>}</p>
              <dl className="dl">
                <dt>Name</dt><dd>{created.name} <span className="mono">({created.id})</span></dd>
                <dt>Icon</dt><dd className="mono">{created.icon}</dd>
                <dt>Description</dt><dd>{created.description || '—'}</dd>
                <dt>Git</dt><dd>{created.remote ? <>branch main, origin <span className="mono">{created.repo}</span></> : 'branch main, local only (no origin)'}</dd>
                <dt>GitHub</dt><dd><GitHubOutcomeCell outcome={created.github} id={created.id} onRetry={(outcome) => setCreated((c) => (c ? { ...c, github: outcome, remote: c.remote || outcome.status === 'pushed' } : c))} /></dd>
                <dt>Ports</dt><dd className="mono">web {created.ports.web} · api {created.ports.api}</dd>
                <dt>URL</dt><dd className="mono">{created.url}</dd>
              </dl>
              <div className="meta">The database is not created yet. <code>npm run app:register apps/{created.id}</code> provisions it.</div>
            </div>
            <div className="os-modal-foot"><Button variant="primary" size="form" onClick={onClose}>Done</Button></div>
          </>
        ) : (
          <form id="create-app-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <div className="os-modal-body">
              <p className="meta" style={{ margin: 0 }}>Copies the app template into <code>apps/&lt;id&gt;</code>, commits it on <code>main</code>, records it in <code>apps.json</code>, and adds it to this list.{github?.configured ? <> Then creates <code>{github.host}/{github.owner}/&lt;id&gt;</code> ({github.visibility}) and pushes <code>main</code> there.</> : null} Roles start as owner and member.</p>
              <Field label="App id" hint="Permanent. Lowercase letters, digits, underscore. Becomes the folder, database name and OIDC client id.">
                <Input autoFocus value={draft.id} spellCheck={false} maxLength={31} className="mono" onChange={(e) => { const id = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 31); set({ id, ...(!iconTouched && { icon: suggestIcon(draft.name, id) }) }); }} />
              </Field>
              <Field label="Display name">
                <Input value={draft.name} maxLength={80} onChange={(e) => { const name = e.target.value; set({ name, ...(!iconTouched && { icon: suggestIcon(name, draft.id) }) }); }} />
              </Field>
              <Field label="Description" hint="Optional. One line stored in os-app.json.">
                <Textarea value={draft.description} rows={2} maxLength={200} onChange={(e) => set({ description: e.target.value })} />
              </Field>
              <Field label="Icon" hint="1–3 letters or digits, shown on the launcher.">
                <Input value={draft.icon} maxLength={3} spellCheck={false} style={{ width: 88, textTransform: 'uppercase' }} onChange={(e) => { setIconTouched(true); set({ icon: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3) }); }} />
              </Field>
              <Field label="Git repository" hint={github?.configured ? `Optional. Leave empty to create and push git@${github.host}:${github.owner}/${draft.id || '<id>'}.git. Another URL is set as origin instead, without a push.` : 'Optional. git@github.com:owner/name.git or an https URL. Sets origin. Leave empty for a local repository only.'}>
                <Input value={draft.repo} spellCheck={false} placeholder={github?.configured ? `git@${github.host}:${github.owner}/${draft.id || 'myapp'}.git` : 'git@github.com:owner/myapp.git'} className="mono" onChange={(e) => set({ repo: e.target.value.trim() })} />
              </Field>
              {error && <p className="os-modal-error" role="alert">{error}</p>}
            </div>
            <div className="os-modal-foot">
              <Button type="button" size="form" onClick={onClose} disabled={create.isPending}>Cancel</Button>
              <Button type="submit" variant="primary" size="form" loading={create.isPending} disabled={create.isPending}>Create</Button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
